import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import logger from "../utils/logger";
import { getSocketUrl, getSocketOptions } from "../utils/socketConfig";

/**
 * Connects to the app socket when the user is logged in. Listens for:
 * - request:new, request:edit_requested (High Potit only)
 * - request:status_updated (all users – approve/reject)
 * - request:updated (all users – admin edited the request in place)
 * Calls onNotify and dispatches window events for in-app toasts and page refetch:
 * - request-notification
 * @param {{ token: string | null, team: string | null, onNotify: (payload: object) => void }} options
 */
export function useRequestNotifications({ token, team, onNotify }) {
  const socketRef = useRef(null);
  const onNotifyRef = useRef(onNotify);
  onNotifyRef.current = onNotify;

  useEffect(() => {
    if (!token || !token.trim()) {
      return;
    }

    const socket = io(getSocketUrl(), getSocketOptions(token));
    socketRef.current = socket;

    const emitNotification = (type, payload = {}, extra = {}) => {
      const notificationPayload = { type, ...payload, ...extra };
      const eventName = "request-notification";

      try {
        onNotifyRef.current?.(notificationPayload);
      } catch (e) {
        logger.error("[useRequestNotifications] onNotify error:", e);
      }
      window.dispatchEvent(
        new CustomEvent(eventName, { detail: notificationPayload }),
      );
    };

    if (team === "high_potit") {
      socket.on("request:new", (data) => {
        const request = data?.request;
        if (request) emitNotification("request:new", { request });
      });

      socket.on("request:edit_requested", (data) => {
        const request = data?.request;
        if (request) emitNotification("request:edit_requested", { request });
      });
    }

    socket.on("request:status_updated", (data) => {
      const request = data?.request;
      const status = data?.status;
      if (request && status) {
        emitNotification("request:status_updated", { request }, { status });
      }
    });

    socket.on("request:updated", (data) => {
      const request = data?.request;
      if (request) emitNotification("request:updated", { request });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, team]);
}

export default useRequestNotifications;
